import moment from 'moment';
import type { PromotionContent } from 'src/models/cms/promotions-content';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CmsContent } from 'src/content';
import { SiteContext } from 'src/context/site-context';
import {
  DEFAULT_TRANSLATION_LANGUAGE_CODE,
  obtainTranslationDataForLanguage,
} from 'src/helpers/combine-translation-data';
import type { LanguageIsoCode } from 'src/enums/language';

const getTimedPromotionForClub = (
  siteId: string,
  promotions: Record<LanguageIsoCode, PromotionContent>[]
) => {
  const now = moment();

  return promotions?.find((promotion) => {
    const timedPromotion = promotion[DEFAULT_TRANSLATION_LANGUAGE_CODE];
    const isPromotionForCurrentClub = timedPromotion.siteIds.includes(siteId);
    const isActive =
      moment(timedPromotion.promotionDetails.to).isAfter(now) &&
      moment(timedPromotion.promotionDetails.from).isBefore(now);

    return isActive && isPromotionForCurrentClub;
  });
};

export const useTimedPromotionForClub = () => {
  const { promotionsContent } = useContext(CmsContent)!;
  const { siteId, language } = useContext(SiteContext);
  const [promotionContent, setPromotionContent] = useState<PromotionContent>();
  const [isReady, setIsReady] = useState<boolean>(false);

  const getTimedPromoActiveForCurrentClubAndLanguage = useCallback(() => {
    const activeTimedPromotionForClub = getTimedPromotionForClub(siteId, promotionsContent);

    if (!activeTimedPromotionForClub) {
      return undefined;
    }

    return obtainTranslationDataForLanguage(activeTimedPromotionForClub, language);
  }, [language, promotionsContent, siteId]);

  // This must execute during client-side render due to the active promotion possibly differing to
  // the one at the time the page was statically generated.
  useEffect(() => {
    setIsReady(true);
    setPromotionContent(getTimedPromoActiveForCurrentClubAndLanguage());
  }, [getTimedPromoActiveForCurrentClubAndLanguage]);

  const useTimedLandingPromo = promotionContent?.promotionDetails?.landing?.applyPromotionContent;
  const useTimedEnquirePromo = promotionContent?.promotionDetails?.enquire?.applyPromotionContent;
  const useTimedPromoPopup = promotionContent?.promotionDetails?.popup?.applyPromotionContent;
  const showGlobalBanner = promotionContent?.promotionDetails?.globalBanner?.showBanner;

  return {
    isTimedPromotionReady: isReady,
    timedPromoActiveForCurrentClub: promotionContent,
    useTimedLandingPromo,
    useTimedEnquirePromo,
    useTimedPromoPopup,
    showGlobalBanner,
  };
};
